import { useQuery } from 'react-query';
import { useEffect } from 'react';
import { useGlobals } from '../../globals-context';
import createQueryConfig from '../../queries';
import useMerchantSettings from '../../hooks/use-merchant-settings';
import { FetchMerchantOnboardingsV2Response } from '@shared/types';

export function useFetchOnboardingSteps() {
  const { queryClient, apiClient, eventBus } = useGlobals()! as any;
  const queryConfig = createQueryConfig(queryClient, apiClient);

  const { merchantSettings } = useMerchantSettings();
  const { merchantId, locale } = merchantSettings || {};

  const queryResult = useQuery<FetchMerchantOnboardingsV2Response>({
    ...queryConfig.merchantOnboardingsV2({ merchantId, locale }),
    enabled: Boolean(merchantId),
    retry: false,
  });

  // Set up event bus listeners to refetch onboarding data conditionally
  useEffect(() => {
    if (!eventBus || !queryClient || !merchantId) return;

    const eventsToListenFor = [
      'company_settings_updated',
      'service_created',
      'appointment_created',
    ];

    const handleConditionalRefetch = () => {
      // Get current onboarding data to check completion status
      const currentData = queryResult.data;

      if (!currentData) {
        // If no data exists yet, allow refetch
        const token =
          queryClient.getQueryData(['merchant_account_token']) || 'loading';
        queryClient.invalidateQueries([
          'merchant_onboardings',
          'v2',
          merchantId,
          String(token),
        ]);
        return;
      }

      // Check if onboarding is completed
      const isCompleted =
        currentData?.overall_progress_percentage === 100 ||
        currentData?.completed_at !== null;

      if (!isCompleted) {
        // Only refetch if onboarding is not completed
        const token =
          queryClient.getQueryData(['merchant_account_token']) || 'loading';
        queryClient.invalidateQueries([
          'merchant_onboardings',
          'v2',
          merchantId,
          String(token),
        ]);
      }
    };

    // Register listeners
    eventsToListenFor.forEach((event) => {
      eventBus.on(event, handleConditionalRefetch);
    });

    // Cleanup listeners on unmount
    return () => {
      eventsToListenFor.forEach((event) => {
        eventBus.off(event, handleConditionalRefetch);
      });
    };
  }, [eventBus, queryClient, merchantId, queryResult.data]);

  return queryResult;
}
